/**
 * HelpPage — public help & support page reachable at `/help`.
 *
 * Includes an FAQ accordion (8 NIPOST-specific questions), contact details,
 * and a deep-link to the complaint form (gated by auth — when not signed in,
 * the link routes via `/auth/login?next=/portal/complaints/new`).
 *
 * Static content; no backend wiring. Lives outside the auth gate so anyone
 * can reach it from the marketing landing page.
 */
import { useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  HelpCircle,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
} from 'lucide-react';

import { useCustomerAuth } from '../../lib/auth/useCustomerAuth';

interface Faq {
  readonly q: string;
  readonly a: string;
}

const FAQS: readonly Faq[] = [
  {
    q: 'How do I track a parcel?',
    a: 'Use the Track a Package link on the home page and enter your tracking number (e.g. EE123456785NG). The status updates as the parcel moves through the network.',
  },
  {
    q: 'How do I book a parcel pickup?',
    a: 'Sign in to your account and open Parcel booking. Provide pickup and delivery addresses, parcel weight, and choose a service class. NIPOST will dispatch a courier on your selected window.',
  },
  {
    q: 'What are the wallet KYC tiers and limits?',
    a: 'Tier 0 (₦20,000 daily inflow) requires only a phone number. Tier 1 (₦200,000) requires BVN. Tier 2 (₦1,000,000) requires NIN + BVN. Tier 3 (institutional) is by application.',
  },
  {
    q: 'How do I verify my NIN to upgrade KYC?',
    a: 'In the Profile page tap "Upgrade KYC" and provide your 11-digit NIN. NIPOST verifies via the IDCentra adapter; verification typically completes within 30 seconds.',
  },
  {
    q: 'How do I file a complaint?',
    a: 'Open the File a complaint button below, choose a category (delivery delay, lost parcel, billing, staff conduct, other), and describe the issue. You\'ll get a reference number to track the response.',
  },
  {
    q: 'My parcel is lost — what now?',
    a: 'File a complaint under "Lost parcel" with the tracking number. Investigations take up to 14 working days. If the parcel is confirmed lost, NIPOST refunds the postage and any declared insurance value.',
  },
  {
    q: 'How do I change my delivery address?',
    a: 'For an in-flight parcel, open Address change from the portal sidebar and submit a request before the parcel reaches its delivery hub. A ₦200 redirection fee may apply.',
  },
  {
    q: 'How do refunds work?',
    a: 'Approved refunds are credited back to your NIPOST wallet within 3 working days. Card-original refunds (back to the card you paid with) take 5–10 working days depending on your bank.',
  },
  {
    q: 'Can I send international parcels?',
    a: 'Yes — EMS Speedpost ships to over 190 countries. Pricing depends on destination and weight; book through Parcel booking and select EMS Speedpost as the service class.',
  },
  {
    q: 'How do I contact customer care?',
    a: 'Phone (+234-9-461-2000), email (support@nipost.gov.ng), or visit any NIPOST outlet. Online complaints get a same-day acknowledgement.',
  },
];

interface AccordionItemProps {
  readonly faq: Faq;
  readonly index: number;
  readonly open: boolean;
  readonly onToggle: () => void;
}

function AccordionItem({ faq, index, open, onToggle }: AccordionItemProps): JSX.Element {
  const id = `faq-${index}`;
  return (
    <div className="border border-border rounded-lg bg-white overflow-hidden">
      <button
        type="button"
        id={`${id}-header`}
        aria-expanded={open}
        aria-controls={`${id}-panel`}
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="text-sm font-semibold text-gray-900">{faq.q}</span>
        <ChevronDown
          className={`w-4 h-4 text-muted flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open ? (
        <div
          id={`${id}-panel`}
          role="region"
          aria-labelledby={`${id}-header`}
          className="px-4 pb-4 text-sm text-gray-700 leading-relaxed"
        >
          {faq.a}
        </div>
      ) : null}
    </div>
  );
}

export function HelpPage(): JSX.Element {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const { token } = useCustomerAuth();
  const complaintHref = token
    ? '/portal/complaints/new'
    : '/auth/login?next=/portal/complaints/new';

  return (
    <div className="space-y-10">
      <header className="text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-nipost-green/10 mb-3">
          <HelpCircle className="w-6 h-6 text-nipost-green" />
        </div>
        <h1 className="text-3xl font-extrabold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
          Help &amp; Support
        </h1>
        <p className="mt-2 text-sm text-muted max-w-xl mx-auto">
          Answers to the questions citizens ask most, and how to reach a human if you need one.
        </p>
      </header>

      {/* FAQs */}
      <section aria-labelledby="faqs-heading">
        <h2 id="faqs-heading" className="text-lg font-bold text-gray-900 mb-4">
          Frequently asked questions
        </h2>
        <div className="space-y-2">
          {FAQS.map((faq, i) => (
            <AccordionItem
              key={faq.q}
              faq={faq}
              index={i}
              open={openIndex === i}
              onToggle={() => setOpenIndex((cur) => (cur === i ? null : i))}
            />
          ))}
        </div>
      </section>

      {/* Contact us */}
      <section aria-labelledby="contact-heading">
        <h2 id="contact-heading" className="text-lg font-bold text-gray-900 mb-4">
          Contact us
        </h2>
        <div className="grid sm:grid-cols-3 gap-3">
          <a
            href="tel:+23494612000"
            className="flex items-start gap-3 p-4 bg-white border border-border rounded-xl hover:border-nipost-green hover:shadow-sm transition-all"
          >
            <div className="w-9 h-9 rounded-lg bg-nipost-green/10 flex items-center justify-center flex-shrink-0">
              <Phone className="w-4 h-4 text-nipost-green" />
            </div>
            <div>
              <p className="text-xs text-muted">Phone</p>
              <p className="text-sm font-semibold text-gray-900">+234-9-461-2000</p>
              <p className="text-[11px] text-muted mt-0.5">Mon–Fri, 8am–6pm WAT</p>
            </div>
          </a>
          <a
            href="mailto:support@nipost.gov.ng"
            className="flex items-start gap-3 p-4 bg-white border border-border rounded-xl hover:border-nipost-green hover:shadow-sm transition-all"
          >
            <div className="w-9 h-9 rounded-lg bg-nipost-green/10 flex items-center justify-center flex-shrink-0">
              <Mail className="w-4 h-4 text-nipost-green" />
            </div>
            <div>
              <p className="text-xs text-muted">Email</p>
              <p className="text-sm font-semibold text-gray-900">support@nipost.gov.ng</p>
              <p className="text-[11px] text-muted mt-0.5">Same-day acknowledgement</p>
            </div>
          </a>
          <div className="flex items-start gap-3 p-4 bg-white border border-border rounded-xl">
            <div className="w-9 h-9 rounded-lg bg-nipost-green/10 flex items-center justify-center flex-shrink-0">
              <MapPin className="w-4 h-4 text-nipost-green" />
            </div>
            <div>
              <p className="text-xs text-muted">Visit</p>
              <p className="text-sm font-semibold text-gray-900">NIPOST HQ</p>
              <p className="text-[11px] text-muted mt-0.5">Garki, Abuja, FCT</p>
            </div>
          </div>
        </div>
      </section>

      {/* File a complaint */}
      <section
        aria-labelledby="complaint-heading"
        className="bg-gradient-to-br from-nipost-green-light to-white border border-nipost-green/20 rounded-2xl p-6"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-nipost-green flex items-center justify-center flex-shrink-0">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 id="complaint-heading" className="text-base font-bold text-gray-900">
                Need to escalate something?
              </h2>
              <p className="text-sm text-muted mt-0.5">
                File a formal complaint and we&apos;ll route it to the right team.
              </p>
            </div>
          </div>
          <Link
            to={complaintHref}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-nipost-green text-white font-semibold rounded-xl hover:bg-nipost-green-dark transition-colors flex-shrink-0"
          >
            File a complaint <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
